import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useState } from 'react'
import { CrossCircledIcon, PlusIcon } from '@radix-ui/react-icons'
import toast from 'react-hot-toast'
import { addMemberInputs, MemberPayload } from '../../types/MemberTypes'
import { useAppDispatch } from '../../reducers/store'
import { addMembers, fetchMembers } from '../../reducers/members/members.thunk'
import { Button } from '@radix-ui/themes'
import { Input } from '../input/Input'
import { addMemberSchema } from '../../schemas/MembersSchema'
import { useSelector } from 'react-redux'
import { selectMembers } from '../../reducers/members'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts'

interface IAddMembersModalProps {
  setShowModal: (show: boolean) => void
}

function AddMembersModal(props: IAddMembersModalProps) {
  const { setShowModal } = props
  const [membersToAdd, setMembersToAdd] = useState<addMemberInputs[]>([])
  const [loading, setLoading] = useState(false)
  const di = useAppDispatch()
  const { filters, memberTableCurrentPage } = useSelector(selectMembers)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<addMemberInputs>({ resolver: yupResolver(addMemberSchema) })

  const onSubmit: SubmitHandler<addMemberInputs> = async (memberToAdd) => {
    if (!membersToAdd.some((member) => member.email.toLowerCase() === memberToAdd.email.toLowerCase())) {
      const newMemberToAdd = { ...memberToAdd }
      setMembersToAdd([...membersToAdd, newMemberToAdd])
      reset()
    } else {
      toast.error('Member already added', DEFAULT_TOAST_CONFIG)
    }
  }

  const addMembersFromForm = async (members: MemberPayload[]) => {
    setLoading(true)
    members.forEach((member, index) => {
      member.id = (index + 1).toString()
    })
    const newMembers = await di(addMembers(members))
    setLoading(false)
    if (newMembers.error) {
      toast.error(newMembers.error, DEFAULT_TOAST_CONFIG)
    } else {
      di(fetchMembers(filters, memberTableCurrentPage))
      toast.success(`Successfully added ${members.length} new member${members.length === 1 ? '' : 's'}`, DEFAULT_TOAST_CONFIG)
      setShowModal(false)
    }
  }

  const removeMember = (email: string) => {
    const newMembers = membersToAdd.filter((member) => member.email !== email)
    setMembersToAdd(newMembers)
  }

  return (
    <div
      className="bg-black bg-opacity-50 absolute top-0 left-0 w-full h-full z-50"
      onClick={() => setShowModal(false)}
    >
      <div
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-modalBackground rounded-lg p-6 max-h-[70%] overflow-y-auto border dark:border-gray-600"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-4">Add Team Members</h2>
        <p className="mb-2">
          An email will be sent to each individual with a unique code to enroll their device in your organization.
          <br />
          {membersToAdd.length > 0 && (
            <i>
              {membersToAdd.length} code will be generated and billing will be updated to reflect the new device count.
            </i>
          )}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="block mt-4">
          <div className="flex">
            <div className="mr-2 w-[180px]">
              <label className="text-secondaryText font-semibold text-xs block pb-1">FULL NAME</label>
              <Input
                type="text"
                placeholder="Full Name"
                {...register('name', { required: true })}
                onChange={undefined}
                maxLength={50}
              />
              {errors.name && <p className="text-ErrorRed">{errors.name.message}</p>}
            </div>
            <div className="w-[272px]">
              <label className="text-secondaryText font-semibold text-xs block pb-1">EMAIL</label>
              <Input
                type="email"
                placeholder="name@example.com"
                {...register('email', { required: true })}
                onChange={undefined}
              />
              {errors.email && <p className="text-ErrorRed">{errors.email.message}</p>}
            </div>
            <Button
              type="submit"
              className="w-[36px] h-[36px] bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple rounded-full mt-[19px] ml-4 p-2.5 text-white font-semibold text-sm cursor-pointer"
              value="Add"
            >
              <PlusIcon />
            </Button>
          </div>
        </form>

        <div className="w-full h-[300px] border border-lightGrey rounded p-3 bg-tableHeaderGrey dark:bg-inputBackground overflow-y-scroll my-4">
          {membersToAdd.length > 0 && (
            <table className="table-auto w-full ">
              <thead>
                <tr>
                  <th className="text-left">Name</th>
                  <th className="text-left">Email</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="max-h-[270px]">
                {membersToAdd.map((member) => (
                  <tr key={member.email}>
                    <td className="pr-2 break-all">{member.name}</td>
                    <td className="pr-2 break-all">{member.email}</td>
                    <td className="text-right text-mainPurple cursor-pointer">
                      <CrossCircledIcon
                        height={20}
                        width={20}
                        className="text-ErrorRed mr-2 cursor-pointer"
                        onClick={() => removeMember(member.email)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex justify-end">
          <button className="px-4 py-2 mr-2 bg-gray-300 rounded text-neutral-12" onClick={() => setShowModal(false)}>
            Cancel
          </button>
          <Button
            className={`h-10 px-4 py-2 ${membersToAdd.length === 0 || loading ? 'opacity-50 cursor-not-allowed' : ' cursor-pointer hover:bg-tertiaryPurple active:active:bg-tertiaryPurple'} bg-mainPurple text-white`}
            onClick={() => {
              addMembersFromForm(membersToAdd)
            }}
            disabled={membersToAdd.length === 0 || loading}
          >
            {loading ? 'Inviting...' : 'Send Invitations'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AddMembersModal
